import { useRef, useState } from 'react';
import { Select } from 'antd';
import type { RefSelectProps } from 'antd';
import apiClient from '@/api/client';

interface LdapUser {
  id: number;
  username: string;
  display_name: string;
  email: string;
  department: string;
}

export interface UserOption {
  value: number;
  label: string;
}

interface LdapUserSelectProps {
  mode?: 'single' | 'multiple';
  value?: UserOption | UserOption[] | null;
  onChange?: (val: UserOption | UserOption[] | null) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
}

const LdapUserSelect = ({
  mode = 'multiple',
  value,
  onChange,
  placeholder,
  disabled,
  allowClear = true,
}: LdapUserSelectProps) => {
  const [options, setOptions] = useState<LdapUser[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectRef = useRef<RefSelectProps>(null);

  const handleSearch = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) return;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiClient.get<LdapUser[]>('/auth/users/search-ldap/', { params: { q } });
        setOptions(res.data || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const selectedIds = new Set<number>(
    mode === 'single'
      ? (value ? [(value as UserOption).value] : [])
      : ((value as UserOption[]) || []).map((v) => v.value)
  );

  const selectOptions = options
    .filter((u) => !selectedIds.has(u.id))
    .map((u) => ({
      value: u.id,
      label: u.username,
      desc: `${u.email}${u.department ? ' · ' + u.department : ''}`,
    }));

  const optionRender = (opt: { data: { label: string; desc: string } }) => (
    <div>
      <span>{opt.data.label}</span>
      <span style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 12 }}>{opt.data.desc}</span>
    </div>
  );

  if (mode === 'single') {
    return (
      <Select
        ref={selectRef}
        showSearch
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder}
        loading={loading}
        filterOption={false}
        labelInValue
        value={value as UserOption | null | undefined}
        onSearch={handleSearch}
        onChange={(val) => onChange?.(val ?? null)}
        onSelect={() => selectRef.current?.blur()}
        options={selectOptions}
        optionRender={optionRender}
      />
    );
  }

  return (
    <Select
      ref={selectRef}
      mode="multiple"
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading}
      filterOption={false}
      labelInValue
      value={(value as UserOption[]) || []}
      onSearch={handleSearch}
      onChange={(vals) => onChange?.(vals)}
      onSelect={() => selectRef.current?.blur()}
      options={selectOptions}
      optionRender={optionRender}
    />
  );
};

export default LdapUserSelect;
